<template>
  <div class="meter-detail">
    <MeterTeaser :meter="meter" />
    <div class="meter-detail__content">
      <TabLinks :tabs="tabs" />
      <component role="tabpanel" :aria-labelledby="`tab_${activeTab.index}`" :is="activeTab.component" :meter="meter" />
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/icons/Spinner.vue';
import MeterTeaser from '@/components/meters/MeterTeaser.vue';
import TabLinks from '@/components/TabLinks.vue';
import { assetPath } from '@/util'
import { useColors } from '@/composables/useColors';
import { useStrings } from '@/composables/useStrings';
import { useUnits } from '@/composables/useUnits';
import { useDeviceStore } from '@/stores/useDeviceStore';
import { computed, defineAsyncComponent, defineComponent, onBeforeMount, ref } from 'vue';
import { useRoute } from 'vue-router';

  const { colorPrimary } = useColors();

  export default defineComponent({
    name: 'meter-detail',
    props: {
      meter: {
        type: Object,
        required: true,
      },
    },
    components: {
      MeterTeaser,
      Spinner,
      TabLinks,
      DeviceConsumption: defineAsyncComponent(() => import('@/components/devices/DeviceConsumption.vue')),
      DeviceTimer: defineAsyncComponent(() => import('@/components/devices/DeviceTimer.vue')),
      MeterSettings: defineAsyncComponent(() => import('@/components/meters/MeterSettings.vue')),
      DeviceClever: defineAsyncComponent(() => import('@/components/devices/DeviceClever.vue')),
    },
    setup(props, { }) {
      const deviceStore = useDeviceStore();
      const { t } = useStrings()
      const route = useRoute();
      const { formatWattage, formatTemperature } = useUnits()
      const name = ref('')
      const description = ref('')
      const mode = ref(false)
      const isOnChangedLocally = ref(false)
      const primaryColor = colorPrimary

      const tabs = computed(() => [
        {
          id: 'consumption',
          label: t('devices.consumption'),
          url: `/devices/efriends/${props.meter.id}/consumption`,
          component: 'DeviceConsumption',
          disabled: props.meter.deviceType !== 'myStrom-Wifi-Switch',
          default: props.meter.deviceType === 'myStrom-Wifi-Switch',
        },
        {
          id: 'timer',
          label: t('devices.timer'),
          url: `/devices/efriends/${props.meter.id}/timer`,
          component: 'DeviceTimer',
          disabled: props.meter.deviceType !== 'myStrom-Wifi-Switch',
        },
        {
          id: 'settings',
          label: 'Details',
          url: `/meters/efriends/${
            props.meter.settings?.inverterHost ||
            props.meter.inverterSettings?.inverterHost ||
            '1'
          }/settings`,
          component: 'MeterSettings',
          disabled: false,
          default: true,
        },
      ])

      const imageUrl = computed(() =>
        assetPath(
          `device-types/${props.meter.deviceType.toLowerCase()}.png`
        )
      )

      const toggleClasses = computed(() => {
        let classes = ['meter-detail__toggle']
        if (isOnChangedLocally.value) { classes.push('meter-detail__toggle--pending') }
        if (props.meter.isOn) { classes.push('meter-detail__toggle--active') }
        return classes.join(' ')
      })

      const toggleLabel = computed(() =>
        props.meter.isOn
          ? t('devices.turnOff')
          : t('devices.turnOn')
      )

      const consumption = computed(() =>
        formatWattage(props.meter.wattage)
      )

      const temperature = computed(() =>
        formatTemperature(props.meter.temperature)
      )

      const activeTab = computed(() => {
        const routerTab = route.params.tab
        return tabs.value
          .filter((tab) => (!routerTab && tab.default) || tab.id === routerTab)
          .map((tab, index) => {
            tab.index = index
            return tab
          })[0]
      })

      const setValuesFromProp = () => {
        if (!props.meter) { return }
        name.value = props.meter.name
        description.value = props.meter.description
        mode.value = props.meter.mode
        isOnChangedLocally.value = props.meter.isOnChangedLocally
      }

      const setDeviceStatus = () => {
        deviceStore.toggleDeviceOn(props.meter)
      }

      onBeforeMount(setValuesFromProp)

      return {
        name,
        description,
        mode,
        isOnChangedLocally,
        primaryColor,
        tabs,
        imageUrl,
        toggleClasses,
        toggleLabel,
        consumption,
        temperature,
        activeTab,
        setDeviceStatus,
        t,
      }
    },
  })
</script>

<style lang="scss">
  .meter-detail {
    justify-content: space-between;
    padding-top: spacer(2);
    flex-wrap: wrap;
    margin-bottom: spacer(3);

    @media (min-width: $bp-xs) {
      display: flex;
    }

    @media (min-width: $bp-md) {
      margin-bottom: spacer(5);
    }

    &__name {
      flex-basis: 100%;
      font-size: $font-size-xl;
      margin-bottom: spacer(0.25);
      display: flex;
      justify-content: space-between;
      align-items: center;
      line-height: $line-height-sm;

      @media (min-width: $bp-sm) {
        font-size: $font-size-huger;
      }
    }

    &__state-unknown {
      .icon {
        width: spacer(2);
        animation: spin 1.8s linear 0s infinite;

        @media (min-width: $bp-sm) {
          width: spacer(3);
        }
      }
    }

    &__general {
      flex-basis: 100%;
      display: flex;
      justify-content: space-between;
      margin-bottom: spacer(1);
      margin-top: spacer(1);
    }

    &__description {
      @media (min-width: $bp-lg) {
        font-size: $font-size-lg;
      }
    }

    &__current {
      text-align: right;
      margin-left: spacer(2);

      &-consumption {
        font-weight: bold;
        font-size: $font-size-lg;
        transition: $transition-base;

        @media (min-width: $bp-md) {
          font-size: $font-size-xl;
        }
      }
    }

    &__img {
      margin-left: auto;
      margin-right: auto;
      max-width: 200px;

      // &--not-reachable {
      //   opacity: 0.5;
      // }

      img {
        max-width: 100%;
      }
    }

    &__not-reachable {
      flex-basis: 100%;
      text-align: center;
      margin: spacer(2) 0;
      color: $error-color;
      font-size: $font-size-lg;

      em {
        font-style: normal;
        font-weight: $font-weight-bold;
      }
    }

    &__content {
      width: 100%;
      margin-top: spacer(3);
    }

    &__mode {
      select {
        width: 100%;
      }
    }

    &__toggle {
      border: none;
      background: none;
      transition: $transition-base;
      margin-left: spacer(0.5);
      width: 3rem;

      @media (min-width: $bp-sm) {
        width: 4rem;
      }

      &:hover,
      &:active {
        color: color.adjust($body-color, $lightness: 7%);
      }

      &--pending {
        animation: pending 1.5s ease-in-out 0s infinite;
      }

      &--active {
        color: $green;

        &:hover,
        &:active {
          color: color.adjust($green, $lightness: -5%);
        }
      }

      .icon {
        display: block;
      }
    }
  }
</style>
